import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Check, X, ArrowRight, Star, ChevronRight, Sparkles } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { getApp, getFeatureTaxonomy, getVersusVerdict } from "@/lib/api";
import { formatPlanPrice, planModel, planEffectiveMonthly } from "@/lib/pricing";
import { planApproximations } from "@/lib/fx";
import { useFxRates } from "@/hooks/useFxRates";
import AdSlot from "@/components/AdSlot";
import AppLogo from "@/components/AppLogo";

// Always render the canonical against the production origin. We deliberately
// avoid `window.location.origin` because Emergent's SEO prerender may snapshot
// from a different host — hardcoding the production domain keeps Googlebot's
// canonical stable no matter which host scraped the page.
const PROD_ORIGIN = "https://horseappcompare.com";

const setPageMeta = ({ title, description, url }) => {
  document.title = title;
  const set = (n, v, attr = "name") => {
    let el = document.querySelector(`meta[${attr}="${n}"]`);
    if (!el) {
      el = document.createElement("meta");
      el.setAttribute(attr, n);
      document.head.appendChild(el);
    }
    el.setAttribute("content", v);
  };
  set("description", description);
  set("og:title", title, "property");
  set("og:description", description, "property");
  set("og:url", url, "property");
  set("og:type", "article", "property");
  let canonical = document.querySelector("link[rel='canonical']");
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.setAttribute("rel", "canonical");
    document.head.appendChild(canonical);
  }
  canonical.setAttribute("href", url);
};


/**
 * AI-generated verdict block — replaces the previous static score-only paragraph.
 *
 * Hits `/api/compare/{a}-vs-{b}/verdict`, which is cached for 30 days in
 * `db.versus_verdicts`. First visitor pays the LLM cost; everyone else
 * (and Googlebot) gets the cached copy instantly.
 *
 * Falls back to the legacy "{winner} wins this head-to-head — narrowly"
 * paragraph if the LLM ever fails, so the page never feels broken.
 */
const AiVerdict = ({ appA, appB, winner, loser, isNl }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setFailed(false);
    getVersusVerdict(appA.slug, appB.slug)
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setFailed(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [appA.slug, appB.slug]);

  // Map verdict_a/verdict_b back to actual slugs (response uses sorted order)
  const aIsLeft = data && data.slug_a === appA.slug;
  const leftVerdict = data ? (aIsLeft ? data.verdict_a : data.verdict_b) : "";
  const rightVerdict = data ? (aIsLeft ? data.verdict_b : data.verdict_a) : "";

  return (
    <div className="mt-10 p-6 md:p-8 bg-gradient-to-br from-[#EFF6FF] via-white to-[#F5F3FF] border border-[#DBEAFE] rounded-2xl" data-testid="ai-verdict">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={14} className="text-[#7C3AED]" />
        <div className="overline text-[#7C3AED]">{isNl ? "AI conclusie" : "AI verdict"}</div>
        {data?.cached && <span className="text-[10px] text-[#94A3B8]">· {isNl ? "in cache" : "cached"}</span>}
      </div>

      {loading && (
        <div className="text-sm text-[#64748B] italic" data-testid="ai-verdict-loading">
          {isNl ? "Conclusie wordt geschreven…" : "Writing the verdict…"}
        </div>
      )}

      {!loading && data && (
        <>
          {data.tldr && (
            <h2 className="font-heading text-2xl md:text-3xl text-[#0F172A] mb-3" data-testid="ai-verdict-tldr">
              {data.tldr}
            </h2>
          )}
          {data.summary && (
            <p className="text-[#1E293B] leading-relaxed mb-4" data-testid="ai-verdict-summary">
              {data.summary}
            </p>
          )}
          <div className="grid md:grid-cols-2 gap-4">
            <div className="bg-white/70 backdrop-blur rounded-lg p-4 border border-white">
              <div className="font-bold text-[#0F172A] mb-1 text-sm">{appA.name}</div>
              <p className="text-sm text-[#475569] leading-relaxed" data-testid="ai-verdict-a">
                {leftVerdict}
              </p>
            </div>
            <div className="bg-white/70 backdrop-blur rounded-lg p-4 border border-white">
              <div className="font-bold text-[#0F172A] mb-1 text-sm">{appB.name}</div>
              <p className="text-sm text-[#475569] leading-relaxed" data-testid="ai-verdict-b">
                {rightVerdict}
              </p>
            </div>
          </div>
        </>
      )}

      {!loading && failed && (
        <>
          <h2 className="font-heading text-2xl md:text-3xl text-[#0F172A] mb-3">
            {isNl
              ? `${winner.name} wint deze head-to-head — net.`
              : `${winner.name} wins this head-to-head — narrowly.`}
          </h2>
          <p className="text-[#1E40AF] leading-relaxed">
            {isNl
              ? `Op basis van onze samengestelde score (${winner.score?.toFixed(1)} vs ${loser.score?.toFixed(1)}) trekt ${winner.name} aan het langste eind. Kies voor ${loser.name} als prijs of een specifieke functie doorslaggevend is.`
              : `Based on our composite score (${winner.score?.toFixed(1)} vs ${loser.score?.toFixed(1)}), ${winner.name} comes out ahead. Pick ${loser.name} if price or a specific feature is your dealbreaker.`}
          </p>
        </>
      )}
    </div>
  );
};


const VersusPage = () => {
  const { matchup } = useParams();
  const { t, lang } = useLang();
  const [appA, setAppA] = useState(null);
  const [appB, setAppB] = useState(null);
  const [cats, setCats] = useState({});
  const [notFound, setNotFound] = useState(false);
  const fxRates = useFxRates();

  const parts = (matchup || "").split("-vs-");
  const slugA = parts[0];
  const slugB = parts[1];

  useEffect(() => {
    if (!slugA || !slugB || slugA === slugB) {
      setNotFound(true);
      return;
    }
    Promise.all([getApp(slugA), getApp(slugB), getFeatureTaxonomy()])
      .then(([a, b, tax]) => {
        setAppA(a);
        setAppB(b);
        setCats(tax.categories || {});
      })
      .catch(() => setNotFound(true));
  }, [slugA, slugB]);

  const title = appA && appB
    ? `${appA.name} vs ${appB.name} — ${lang === "nl" ? "Vergelijking 2026" : "2026 Comparison"}`
    : `${slugA} vs ${slugB} — HorseAppCompare`;
  const description = appA && appB
    ? `${appA.name} vs ${appB.name}: ${lang === "nl" ? "vergelijk functies, prijzen, AI, platformen en doelgroep op HorseAppCompare." : "compare features, pricing, AI, platforms and audience on HorseAppCompare."}`
    : "Head-to-head horse app comparison on HorseAppCompare.";
  // Both /versus/X-vs-Y and /compare/X-vs-Y render the same page; we canonicalise
  // to /compare/X-vs-Y because "X vs Y" is more search-volume on that path.
  const url = `${PROD_ORIGIN}/compare/${matchup}`;
  useEffect(() => {
    setPageMeta({ title, description, url });
  }, [title, description, url]);

  // Critical for SEO: do NOT redirect on notFound — that would let Emergent's
  // prerender service snapshot /apps and inherit its canonical, breaking the
  // compare page's own canonical link. Render an inline 404 instead so the
  // canonical/title/description in <head> are still pointing at THIS URL.
  if (notFound) {
    return (
      <main className="hac-container py-20 text-center" data-testid="versus-not-found">
        <h1 className="font-heading text-3xl text-[#0F172A] mb-3">Comparison not found</h1>
        <p className="text-[#64748B] mb-6">We couldn't find the apps "{slugA}" or "{slugB}". They may have been removed or never existed.</p>
        <Link to="/apps" className="hac-btn-primary">Browse the directory</Link>
      </main>
    );
  }
  if (!appA || !appB) return <main className="hac-container py-20 text-center text-[#94A3B8]">…</main>;

  // Diff features
  const fa = new Set(appA.features || []);
  const fb = new Set(appB.features || []);
  const onlyA = [];
  const onlyB = [];
  const both = [];
  Object.entries(cats).forEach(([cat, feats]) => {
    feats.forEach((f) => {
      if (fa.has(f) && fb.has(f)) both.push({ cat, f });
      else if (fa.has(f)) onlyA.push({ cat, f });
      else if (fb.has(f)) onlyB.push({ cat, f });
    });
  });

  const winner = (appA.score || 0) >= (appB.score || 0) ? appA : appB;
  const loser = winner === appA ? appB : appA;
  const isNl = lang === "nl";

  // Structured data
  const breadcrumbLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${PROD_ORIGIN}/` },
      { "@type": "ListItem", position: 2, name: isNl ? "Vergelijken" : "Compare", item: `${PROD_ORIGIN}/compare` },
      { "@type": "ListItem", position: 3, name: `${appA.name} vs ${appB.name}`, item: url },
    ],
  };
  const buildAppLd = (a) => ({
    "@type": "SoftwareApplication",
    name: a.name,
    operatingSystem: [a.platforms?.ios && "iOS", a.platforms?.android && "Android", a.platforms?.web && "Web"].filter(Boolean).join(", "),
    applicationCategory: "LifestyleApplication",
    offers: { "@type": "Offer", price: a.pricing?.monthly_usd || 0, priceCurrency: "USD" },
    aggregateRating: { "@type": "AggregateRating", ratingValue: a.rating, reviewCount: a.review_count || 1 },
    url: `${PROD_ORIGIN}/apps/${a.slug}`,
  });
  const comparisonLd = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: title,
    itemListElement: [
      { "@type": "ListItem", position: 1, item: buildAppLd(appA) },
      { "@type": "ListItem", position: 2, item: buildAppLd(appB) },
    ],
  };

  const SideHeader = ({ a, winnerFlag }) => (
    <div className="flex-1 text-center p-6 md:p-8 bg-white relative">
      {winnerFlag && (
        <div className="absolute top-3 left-1/2 -translate-x-1/2 bg-[#2563EB] text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full">
          {isNl ? "Onze keuze" : "Editor's pick"}
        </div>
      )}
      <Link to={`/apps/${a.slug}`}>
        <div className="mx-auto mb-3 inline-block"><AppLogo app={a} className="w-16 h-16 rounded-2xl" textClassName="text-2xl" /></div>
        <h2 className="font-heading text-2xl md:text-3xl text-[#0F172A] hover:text-[#2563EB] transition-colors">{a.name}</h2>
      </Link>
      <div className="text-xs text-[#94A3B8] mt-1">{a.country}</div>
      <div className="flex items-center justify-center gap-1 mt-3 text-sm">
        <Star size={14} className="fill-[#2563EB] text-[#2563EB]" />
        <span className="font-mono text-[#0F172A]">{a.rating?.toFixed(1)}</span>
        <span className="text-[#94A3B8]">· {a.review_count} {isNl ? "reviews" : "reviews"}</span>
      </div>
      <div className="font-heading text-2xl text-[#0F172A] mt-3">
        {a.pricing?.free_tier && a.pricing?.monthly_usd === 0 ? (isNl ? "Gratis" : "Free") : `$${a.pricing?.monthly_usd}`}
        {a.pricing?.monthly_usd > 0 && <span className="text-sm text-[#64748B]">{isNl ? "/mnd" : "/mo"}</span>}
      </div>
    </div>
  );

  return (
    <main>
      <section className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
        <div className="hac-container py-12 md:py-20 max-w-5xl">
          <nav className="flex items-center gap-2 text-xs text-[#64748B] mb-6">
            <Link to="/" className="hover:text-[#2563EB]">Home</Link>
            <ChevronRight size={12} />
            <Link to="/compare" className="hover:text-[#2563EB]">{isNl ? "Vergelijken" : "Compare"}</Link>
            <ChevronRight size={12} />
            <span className="text-[#0F172A] font-medium">{appA.name} vs {appB.name}</span>
          </nav>
          <div className="overline mb-3 text-[#2563EB]">{isNl ? "Head-to-head" : "Head-to-head"}</div>
          <h1 className="font-heading text-4xl md:text-6xl text-[#0F172A] leading-[1.05] mb-5">
            {appA.name} vs {appB.name}: {isNl ? "Welke paardenapp is beter in 2026?" : "Which Horse App Is Better in 2026?"}
          </h1>
          <p className="text-lg text-[#64748B] leading-relaxed max-w-3xl">
            {isNl
              ? `We zetten ${appA.name} en ${appB.name} naast elkaar op functies, prijs, AI, platformen en doelgroep — gebaseerd op de publieke release van beide apps.`
              : `We put ${appA.name} and ${appB.name} side by side on features, pricing, AI, platforms and audience — based on the public release of both apps.`}
          </p>
        </div>
      </section>

      <section className="hac-container py-16 max-w-5xl">
        <div className="grid md:grid-cols-2 border border-[#E2E8F0] rounded-2xl overflow-hidden divide-y md:divide-y-0 md:divide-x divide-[#E2E8F0]">
          <SideHeader a={appA} winnerFlag={winner === appA} />
          <SideHeader a={appB} winnerFlag={winner === appB} />
        </div>

        {/* AI Verdict — Claude-generated, cached 30 days in db.versus_verdicts */}
        <AiVerdict appA={appA} appB={appB} winner={winner} loser={loser} isNl={isNl} />
      </section>

      <AdSlot placement="compare-top" slugs={[appA.slug, appB.slug]} />

      {/* Feature diff */}
      <section className="hac-container py-12 max-w-5xl">
        <h2 className="font-heading text-2xl md:text-4xl text-[#0F172A] leading-tight mb-8">
          {isNl ? "Wat heeft de één wel en de ander niet?" : "What does one have that the other doesn't?"}
        </h2>
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <div className="border border-[#E2E8F0] rounded-2xl p-6 bg-white">
            <h3 className="font-bold text-lg text-[#0F172A] mb-4">
              {isNl ? `Alleen in ${appA.name}` : `Only in ${appA.name}`}
            </h3>
            {onlyA.length === 0 ? (
              <p className="text-sm text-[#94A3B8]">{isNl ? "Geen unieke functies." : "No unique features."}</p>
            ) : (
              <ul className="space-y-1.5">
                {onlyA.slice(0, 15).map(({ cat, f }) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-[#0F172A]">
                    <Check size={14} className="text-[#2563EB] mt-1 shrink-0" />
                    {t.features[f] || f} <span className="text-[#94A3B8] text-xs ml-1">· {t.categories[cat]}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="border border-[#E2E8F0] rounded-2xl p-6 bg-white">
            <h3 className="font-bold text-lg text-[#0F172A] mb-4">
              {isNl ? `Alleen in ${appB.name}` : `Only in ${appB.name}`}
            </h3>
            {onlyB.length === 0 ? (
              <p className="text-sm text-[#94A3B8]">{isNl ? "Geen unieke functies." : "No unique features."}</p>
            ) : (
              <ul className="space-y-1.5">
                {onlyB.slice(0, 15).map(({ cat, f }) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-[#0F172A]">
                    <Check size={14} className="text-[#2563EB] mt-1 shrink-0" />
                    {t.features[f] || f} <span className="text-[#94A3B8] text-xs ml-1">· {t.categories[cat]}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="border border-[#E2E8F0] rounded-2xl p-6 bg-[#F1F5F9]">
          <h3 className="font-bold text-lg text-[#0F172A] mb-3">
            {isNl ? "Wat ze gemeen hebben" : "What they share"} ({both.length})
          </h3>
          <div className="flex flex-wrap gap-1.5">
            {both.slice(0, 24).map(({ f }) => (
              <span key={f} className="hac-chip text-xs">{t.features[f] || f}</span>
            ))}
            {both.length > 24 && <span className="hac-chip text-xs">+{both.length - 24}</span>}
          </div>
        </div>
      </section>

      {/* Pricing tiers side-by-side — primary SEO content for "X vs Y price" queries */}
      {(Array.isArray(appA.plans) || Array.isArray(appB.plans)) && (
        <section className="hac-container py-12 max-w-5xl" data-testid="versus-plans-section">
          <h2 className="font-heading text-2xl md:text-4xl text-[#0F172A] leading-tight mb-3">
            {isNl ? `${appA.name} vs ${appB.name}: prijzen & modules` : `${appA.name} vs ${appB.name}: pricing & modules`}
          </h2>
          <p className="text-[#64748B] mb-8 max-w-2xl">
            {isNl
              ? "Vergelijk per app welke modules in elk prijsplan zitten. Beheerd door de app-eigenaren zelf."
              : "Compare which modules each app bundles in every pricing tier. Managed by each app owner."}
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            {[appA, appB].map((a) => {
              const plans = Array.isArray(a.plans) ? a.plans : [];
              const hasFree = plans.some((p) => planModel(p) === "free");
              const paidPlans = plans
                .map((p) => ({ p, eff: planEffectiveMonthly(p) }))
                .filter((x) => x.eff > 0)
                .sort((x, y) => x.eff - y.eff);
              const cheapestPaid = paidPlans[0]?.p;
              return (
                <div key={a.slug} className="bg-white border border-[#E2E8F0] rounded-2xl p-6" data-testid={`versus-plans-${a.slug}`}>
                  <div className="flex items-center gap-3 mb-3">
                    <AppLogo app={a} className="w-10 h-10 rounded-lg" textClassName="text-base" />
                    <div className="font-heading text-lg text-[#0F172A]">{a.name}</div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {plans.length > 0 && (hasFree ? (
                      <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-700 bg-emerald-100 border border-emerald-200 px-2 py-0.5 rounded-full">
                        ✓ {isNl ? "Gratis versie" : "Free tier"}
                      </span>
                    ) : (
                      <span className="text-[10px] font-bold uppercase tracking-widest text-[#64748B] bg-[#F1F5F9] border border-[#E2E8F0] px-2 py-0.5 rounded-full">
                        {isNl ? "Geen gratis versie" : "Paid only"}
                      </span>
                    ))}
                    {cheapestPaid && (
                      <span className="text-[10px] font-bold uppercase tracking-widest text-[#2563EB] bg-[#EFF6FF] border border-[#DBEAFE] px-2 py-0.5 rounded-full">
                        {isNl ? "Vanaf" : "From"} {formatPlanPrice(cheapestPaid, lang).primary}
                      </span>
                    )}
                  </div>
                  {plans.length === 0 ? (
                    <p className="text-sm text-[#94A3B8] italic">
                      {isNl ? "Deze app-eigenaar heeft nog geen prijsplannen gedeeld." : "This app owner hasn't shared pricing tiers yet."}
                    </p>
                  ) : (
                    <ul className="space-y-3">
                      {plans.map((p, i) => {
                        const { primary, isFree } = formatPlanPrice(p, lang);
                        const approx = isFree ? [] : planApproximations(p, fxRates, lang);
                        return (
                          <li key={`${p.name}-${i}`} className={`border rounded-lg p-3 ${p.popular ? "border-amber-400 bg-amber-50/40" : "border-[#E2E8F0]"}`}>
                            <div className="flex items-baseline justify-between gap-2 mb-1">
                              <div className="font-bold text-[#0F172A] inline-flex items-center gap-1.5">
                                {p.popular && <Star size={11} className="fill-amber-500 text-amber-500" />}
                                {p.name}
                              </div>
                              <div className={`font-mono text-[#0F172A] whitespace-nowrap ${isFree ? "text-sm" : "text-xs"}`}>
                                {primary}
                              </div>
                            </div>
                            {approx.length > 0 && (
                              <div
                                className="text-[10px] text-[#94A3B8] font-mono text-right mb-1.5 -mt-1"
                                title={lang === "nl" ? "Indicatieve omrekening — koers via ECB" : "Approximate — daily ECB rate"}
                                data-testid={`versus-plan-approx-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                              >
                                {approx.join(" · ")}
                              </div>
                            )}
                            {p.description && <p className="text-[11px] text-[#64748B] mb-2 leading-snug">{p.description}</p>}
                            {(p.features || []).length > 0 && (
                              <ul className="space-y-0.5 text-[11px] text-[#0F172A]">
                                {p.features.slice(0, 6).map((f, fi) => (
                                  <li key={fi} className="flex items-start gap-1.5">
                                    <Check size={10} className="text-emerald-600 mt-1 shrink-0" />
                                    <span>{f}</span>
                                  </li>
                                ))}
                                {p.features.length > 6 && (
                                  <li className="text-[10px] text-[#94A3B8] italic pl-4">+ {p.features.length - 6} {isNl ? "meer" : "more"}</li>
                                )}
                              </ul>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                  <Link to={`/apps/${a.slug}#plans`} className="mt-4 inline-flex items-center gap-1 text-xs font-bold text-[#2563EB] hover:text-[#1D4ED8] hover:gap-1.5 transition-all" data-testid={`versus-plans-review-${a.slug}`}>
                    {isNl ? `Bekijk volledige ${a.name} review` : `View full ${a.name} review`} <ArrowRight size={11} />
                  </Link>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* When to pick each */}
      <section className="bg-[#F1F5F9] border-y border-[#E2E8F0] py-16">
        <div className="hac-container max-w-5xl grid md:grid-cols-2 gap-6">
          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6">
            <h3 className="font-heading text-2xl text-[#0F172A] mb-3">
              {isNl ? `Kies ${appA.name} als…` : `Pick ${appA.name} if…`}
            </h3>
            <ul className="space-y-2 text-[#64748B] text-sm">
              <li className="flex items-start gap-2"><ArrowRight size={14} className="text-[#2563EB] mt-1 shrink-0" />
                {isNl ? `Je past in: ${appA.target_audience?.join(", ")}` : `You match: ${appA.target_audience?.join(", ")}`}
              </li>
              <li className="flex items-start gap-2"><ArrowRight size={14} className="text-[#2563EB] mt-1 shrink-0" />
                {isNl ? `Je hebt nodig: ${onlyA.slice(0,3).map(x => t.features[x.f] || x.f).join(", ") || "een betrouwbare basis"}` : `You need: ${onlyA.slice(0,3).map(x => t.features[x.f] || x.f).join(", ") || "a reliable baseline"}`}
              </li>
              <li className="flex items-start gap-2"><ArrowRight size={14} className="text-[#2563EB] mt-1 shrink-0" />
                {isNl ? `Beschikbaar in: ${appA.regions?.join(", ")}` : `Available in: ${appA.regions?.join(", ")}`}
              </li>
            </ul>
            <Link to={`/apps/${appA.slug}`} className="hac-btn-primary mt-5">
              {isNl ? "Bekijk profiel" : "View profile"} <ArrowRight size={14} />
            </Link>
          </div>
          <div className="bg-white border border-[#E2E8F0] rounded-2xl p-6">
            <h3 className="font-heading text-2xl text-[#0F172A] mb-3">
              {isNl ? `Kies ${appB.name} als…` : `Pick ${appB.name} if…`}
            </h3>
            <ul className="space-y-2 text-[#64748B] text-sm">
              <li className="flex items-start gap-2"><ArrowRight size={14} className="text-[#2563EB] mt-1 shrink-0" />
                {isNl ? `Je past in: ${appB.target_audience?.join(", ")}` : `You match: ${appB.target_audience?.join(", ")}`}
              </li>
              <li className="flex items-start gap-2"><ArrowRight size={14} className="text-[#2563EB] mt-1 shrink-0" />
                {isNl ? `Je hebt nodig: ${onlyB.slice(0,3).map(x => t.features[x.f] || x.f).join(", ") || "een betrouwbare basis"}` : `You need: ${onlyB.slice(0,3).map(x => t.features[x.f] || x.f).join(", ") || "a reliable baseline"}`}
              </li>
              <li className="flex items-start gap-2"><ArrowRight size={14} className="text-[#2563EB] mt-1 shrink-0" />
                {isNl ? `Beschikbaar in: ${appB.regions?.join(", ")}` : `Available in: ${appB.regions?.join(", ")}`}
              </li>
            </ul>
            <Link to={`/apps/${appB.slug}`} className="hac-btn-primary mt-5">
              {isNl ? "Bekijk profiel" : "View profile"} <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      <AdSlot placement="compare-middle" slugs={[appA.slug, appB.slug]} />

      {/* CTA */}
      <section className="hac-container py-16 max-w-4xl text-center">
        <p className="text-[#64748B] mb-5">
          {isNl ? "Geen van beide overtuigt? Vergelijk meer apps of vraag onze adviseur." : "Neither convincing? Compare more apps or ask our advisor."}
        </p>
        <div className="flex flex-wrap gap-3 justify-center">
          <Link to="/compare" className="hac-btn-primary">{isNl ? "Vergelijk meer apps" : "Compare more apps"} <ArrowRight size={14} /></Link>
          <Link to="/advisor" className="hac-btn-secondary">{isNl ? "Vraag de adviseur" : "Ask the advisor"}</Link>
        </div>
      </section>

      <AdSlot placement="compare-bottom" slugs={[appA.slug, appB.slug]} />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(comparisonLd) }} />
    </main>
  );
};

export default VersusPage;
